import { useRef, SyntheticEvent, RefObject } from 'react';

import { useIsLoggedIn } from '@zoopla/user';
import { triggerTracking } from '@zoopla/tracking';
import { useRouter } from 'next/router';

import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { Image } from '@zoopla/image';
import { Link } from '@zoopla-eng/zds-components/link';

import {
    footerButtonStyle,
    buttonWrapperStyle,
    couchImageStyle,
    footerContainerStyle,
    footerContentStyle,
    footerDescriptionStyle,
    signInLinkWrapperStyle,
    titleStyle
} from './HomepageRegistrationFooter.styles.css';
import CouchIllustration from '../../assets/couch-illustration.svg';

const headingTitle = 'Join the millions getting more from their home';
const description =
    'With Zoopla, you can track properties you’re interested in and use our price estimates to help you decide when to move. And get expert tips on making the most of your space.';
const register = 'Register';
const signIn = 'Sign in';

const homeValuesHeading = 'Get the most from your home';
const seeMoreOnHomeValues = 'See more on home values';
const homeValuesCopy =
    'Track the value of your home over time and compare it to others in the area to help you figure out the best time to move.';

type Props = {
    openAuthModal: (
        triggerOrigin: RefObject<HTMLElement> | null,
        mode: 'register' | 'sign-in'
    ) => void;
};

export const HomepageRegistrationFooter = ({ openAuthModal }: Props) => {
    const isLoggedIn = useIsLoggedIn();
    const registerTriggerOrigin = useRef(null);
    const signInTriggerOrigin = useRef(null);
    const router = useRouter();

    const registerTracking = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'homepage',
            eventAction: 'registration_footer',
            eventLabel: 'register'
        });
    };

    const signInTracking = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'homepage',
            eventAction: 'registration_footer',
            eventLabel: 'sign_in'
        });
    };

    const handleClickRegister = async () => {
        registerTracking();
        openAuthModal(registerTriggerOrigin, 'register');
    };

    const handleClickSignIn = () => {
        signInTracking();
        openAuthModal(signInTriggerOrigin, 'sign-in');
    };

    const handleClickHomeValues = () => {
        router.push(`https://www.zoopla.co.uk/home-values`);
    };

    return (
        <div className={footerContainerStyle}>
            <div className={footerContentStyle}>
                {!isLoggedIn ? (
                    <>
                        <h2 className={`${fontGroups.mug} ${titleStyle}`}>
                            {headingTitle}
                        </h2>
                        <p
                            className={`${fontGroups.ladle} ${footerDescriptionStyle}`}
                        >
                            {description}
                        </p>
                        <div className={buttonWrapperStyle}>
                            <a
                                className={footerButtonStyle}
                                href="/register/"
                                ref={registerTriggerOrigin}
                                onClick={(event: SyntheticEvent) => {
                                    event.preventDefault();
                                    handleClickRegister();
                                }}
                                data-testid="register-button"
                            >
                                {register}
                            </a>
                            <div className={signInLinkWrapperStyle}>
                                <Link
                                    href="/signin/"
                                    contrasted
                                    ref={signInTriggerOrigin}
                                    data-testid="sign-in-button"
                                    onClick={(event: SyntheticEvent) => {
                                        event.preventDefault();
                                        handleClickSignIn();
                                    }}
                                >
                                    {signIn}
                                </Link>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <h2 className={`${titleStyle} ${fontGroups.mug}`}>
                            {homeValuesHeading}
                        </h2>
                        <p
                            className={`${fontGroups.ladle} ${footerDescriptionStyle}`}
                        >
                            {homeValuesCopy}
                        </p>
                        <div className={buttonWrapperStyle}>
                            <a
                                href="/home-values"
                                className={footerButtonStyle}
                                onClick={(event: SyntheticEvent) => {
                                    event.preventDefault();
                                    handleClickHomeValues();
                                }}
                                data-testid="home-values-button"
                            >
                                {seeMoreOnHomeValues}
                            </a>
                        </div>
                    </>
                )}
            </div>
            <Image
                className={couchImageStyle}
                src={CouchIllustration}
                alt=""
                lazy
            />
        </div>
    );
};
